import { html, render } from 'lit-html'
import { allPomodorosForUser } from '../db.js'
import { currentUser } from '../auth.js'
import './spinner.js'
import { format, sub, add, isToday } from 'date-fns'
import { pomsForDate } from '../filterPoms.js'
import type { User, Pomodoro } from '../types'
import styles from './history.css'

export class PomodoroHistory extends HTMLElement {
  shadow: ShadowRoot
  allUserPoms: Map<string, Pomodoro> | null = null
  loaded: boolean = false
  currentDate: Date = new Date()
  userSubscription: (() => void) | null = null

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
  }

  connectedCallback(): void {
    this.userSubscription = currentUser.subscribe((user) => {
      if (user === undefined) {
        return
      }

      this.loaded = false
      if (user) {
        this.loadAllPomodoros(user)
      } else if (user === null) {
        this.allUserPoms = null
        this.loaded = true
      }
      this.render()
    })
    this.render()
  }

  disconnectedCallback(): void {
    if (this.userSubscription) {
      this.userSubscription()
    }
  }

  loadAllPomodoros(user: User): void {
    allPomodorosForUser(user.uid, (data) => {
      this.allUserPoms = data
      this.loaded = true
      this.render()
    })
  }

  goBackOneDay(): void {
    this.currentDate = sub(this.currentDate, { days: 1 })
    this.render()
  }

  goForwardsOneDay(): void {
    this.currentDate = add(this.currentDate, { days: 1 })
    this.render()
  }

  goToToday(): void {
    this.currentDate = new Date()
    this.render()
  }

  render(): void {
    const currentDayPoms = pomsForDate(
      this.allUserPoms ?? new Map(),
      this.currentDate
    )

    const template = html`
      <style>
        ${styles}
      </style>
      ${this.loaded === false
        ? html`<pomodoro-spinner></pomodoro-spinner>`
        : html`
            <h4>
              ${this.allUserPoms
                ? html`
                    <span class="header-flex">
                      Poms from ${format(this.currentDate, 'do MMMM yyyy')}

                      <div class="date-nav-links">
                        <button @click=${this.goBackOneDay}>
                          &lt;&lt; Prev
                        </button>
                        <button
                          @click=${this.goToToday}
                          ?disabled=${isToday(this.currentDate)}
                        >
                          Back to today
                        </button>
                        <button
                          @click=${this.goForwardsOneDay}
                          ?disabled=${isToday(this.currentDate)}
                        >
                          Next &gt;&gt;
                        </button>
                      </div>
                    </span>
                  `
                : 'Sign in to track your Poms!'}
            </h4>

            ${this.allUserPoms
              ? html`
                  <ul class="poms-list">
                    ${[...currentDayPoms.values()].map((value, index) => {
                      const isManual = value.type === 'MANUAL'
                      return html`
                        <li
                          class="${isManual ? 'manual-pom' : 'timer-pom'}"
                          title="Finished at ${format(
                            new Date(value.timeFinished),
                            'H:mm:ss'
                          )} (${isManual ? 'Manual' : 'Timer'} Pomodoro)"
                        >
                          <div class="pom-header">
                            <span class="counter">${index + 1}</span>
                            <span class="duration">
                              ${isManual ? 'Manual' : `${value.length / 60}`}
                            </span>
                            ${value.walkingPad
                              ? html`<span class="standing-desk-indicator" title="Done at walking pad">🚶</span>`
                              : value.standingDesk
                              ? html`<span class="standing-desk-indicator" title="Done at standing desk">🧍</span>`
                              : ''}
                          </div>
                          ${value.summary
                            ? html`
                                <div class="pom-summary">${value.summary}</div>
                              `
                            : ''}
                        </li>
                      `
                    })}
                  </ul>
                `
              : ''}
          `}
    `
    render(template, this.shadow, { host: this })
  }
}

customElements.define('pomodoro-history', PomodoroHistory)
